<template>
  <v-container fluid class="pa-3" data-tour="kb-board">
    <!-- Histórico do kanban: título e totais à esquerda, o dia à direita. -->
    <div class="kanban-toolbar">
      <div class="kanban-toolbar__title">
        <v-icon color="primary" class="mr-2">
          {{ isHistory ? "mdi-history" : "mdi-view-column-outline" }}
        </v-icon>
        {{ isHistory ? "Histórico do dia" : "Quadro de hoje" }}
        <span v-if="isHistory" class="kanban-toolbar__badge">
          Somente leitura
        </span>
      </div>

      <KanbanDaySummary
        v-if="isHistory"
        :events="boardEvents"
        class="kanban-toolbar__summary"
      />

      <KanbanDayPicker
        v-model="selectedDate"
        :today="today"
        class="kanban-toolbar__picker"
      />
    </div>

    <v-row dense>
      <v-col
        v-for="(column, index) in columns"
        :key="index"
        cols="12"
        sm="6"
        md="3"
        class="kanban-col"
      >
        <v-card
          class="kanban-column-card"
          elevation="2"
          :data-tour="index === 0 ? 'kb-colunas' : null"
        >
          <v-card-title class="column-header" :class="column.colorClass">
            <div class="d-flex align-center justify-space-between w-100">
              <div class="d-flex align-center">
                <v-icon :color="column.iconColor" class="mr-2">
                  {{ column.icon }}
                </v-icon>
                <span class="font-weight-bold">{{ column.title }}</span>
              </div>
              <v-chip :color="column.chipColor" small dark>
                {{ column.tasks.length }}
              </v-chip>
            </div>
          </v-card-title>

          <div class="kanban-column-content">
            <draggable
              :list="column.tasks"
              group="tasks"
              :animation="200"
              ghost-class="ghost-card"
              class="draggable-list"
              @end="onDragEnd"
              :data-status="column.status"
              :disabled="isHistory"
            >
              <v-card
                v-for="(task, ti) in column.tasks"
                :key="task.id"
                class="task-card"
                :class="{ 'task-card--readonly': isHistory }"
                elevation="3"
                :data-tour="index === 0 && ti === 0 ? 'kb-card' : null"
                @click="openHistoryEvent(task)"
              >
                <v-card-title class="pa-3 pb-2">
                  <v-list-item class="pa-0">
                    <v-list-item-avatar size="40" v-if="task.patient">
                      <v-img :src="task.patient.photo" :alt="task.patient.name">
                        <template v-slot:placeholder>
                          <v-icon>mdi-account-circle</v-icon>
                        </template>
                      </v-img>
                    </v-list-item-avatar>

                    <v-list-item-content class="py-0">
                      <v-list-item-title
                        class="text-subtitle-2 font-weight-bold"
                      >
                        {{ task.patient ? task.patient.name : "Sem paciente" }}
                      </v-list-item-title>

                      <v-list-item-subtitle
                        v-if="task.patient && task.patient.birth_date"
                        class="text-caption"
                      >
                        {{ calculateAge(task.patient.birth_date) }} anos
                      </v-list-item-subtitle>
                    </v-list-item-content>
                  </v-list-item>
                </v-card-title>

                <v-divider />

                <v-card-text class="pa-3">
                  <div class="info-row mb-2">
                    <v-icon small color="primary" class="mr-2">
                      mdi-calendar
                    </v-icon>
                    <span class="text-caption">
                      <strong>{{ formatDate(task.start) }}</strong>
                    </span>
                  </div>

                  <div class="info-row mb-2">
                    <v-icon small color="primary" class="mr-2">
                      mdi-clock-outline
                    </v-icon>
                    <span class="text-caption">
                      {{ formatTime(task.start) }} -
                      {{ formatTime(task.end) }}
                    </span>
                  </div>

                  <div class="info-row mb-2" v-if="task.clinic">
                    <v-icon small color="primary" class="mr-2">
                      mdi-map-marker
                    </v-icon>
                    <span class="text-caption">{{ task.clinic.name }}</span>
                  </div>

                  <div class="info-row">
                    <v-icon small color="primary" class="mr-2">
                      mdi-stethoscope
                    </v-icon>
                    <span class="text-caption">
                      {{ extractConsultationType(task.description) }}
                    </span>
                  </div>
                </v-card-text>

                <v-divider />

                <v-card-actions class="pa-2 justify-center">
                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="success"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openWhatsApp(task)"
                      >
                        <v-icon small>mdi-whatsapp</v-icon>
                      </v-btn>
                    </template>
                    <span>WhatsApp</span>
                  </v-tooltip>

                  <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="primary"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="openPatientProfile(task)"
                      >
                        <v-icon small>mdi-account-file-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Perfil do Paciente</span>
                  </v-tooltip>

                  <v-tooltip v-if="!isHistory" top>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn
                        icon
                        small
                        color="info"
                        v-bind="attrs"
                        v-on="on"
                        @click.stop="markAsCompleted(task)"
                      >
                        <v-icon small>mdi-calendar-check</v-icon>
                      </v-btn>
                    </template>
                    <span>Marcar como Concluído</span>
                  </v-tooltip>
                </v-card-actions>
              </v-card>
            </draggable>

            <div v-if="column.tasks.length === 0" class="empty-state pa-4">
              <v-icon size="48" color="grey lighten-2">
                {{ column.emptyIcon }}
              </v-icon>
              <p class="text-caption grey--text mt-2">{{ column.emptyText }}</p>
            </div>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <KanbanHistoryEventDialog v-model="historyDialog" :event="historyEvent" />
  </v-container>
</template>

<script>
import draggable from "vuedraggable";
import http from "@/services/http";
import { getProfileImage } from "@/utils/getProfileImage.js";
import { useSnackbar } from "@/composables/useSnackbar";
import tourMixin from "@/features/tour/tourMixin";
import { formatDateShort, formatTime, todayISO, userTimeZone } from "@/utils/datetime";
import { calculateAge } from "@/utils/calculateAge.js";
import { initEcho } from "@/plugins/echo";
import { fetchKanbanDay } from "@/services/kanbanHistoryService";
import KanbanDayPicker from "@/features/calendar/components/KanbanDayPicker.vue";
import KanbanDaySummary from "@/features/calendar/components/KanbanDaySummary.vue";
import KanbanHistoryEventDialog from "@/features/calendar/components/KanbanHistoryEventDialog.vue";

export default {
  name: "AppointmentsKanban",

  mixins: [tourMixin],

  components: { draggable, KanbanDayPicker, KanbanDaySummary, KanbanHistoryEventDialog },

  setup() {
    const { success, error, warning, info, erroDeRequisicao } = useSnackbar();
    return { success, error, warning, info, erroDeRequisicao };
  },

  data() {
    return {
      // Histórico do kanban: abre sempre em hoje; outro dia é só leitura.
      today: todayISO(),
      selectedDate: todayISO(),
      // Os eventos do quadro, na ordem do dia — base do resumo.
      boardEvents: [],
      historyDialog: false,
      historyEvent: null,
      // Descarta respostas atrasadas quando o médico troca de dia rápido.
      loadSeq: 0,
      columns: [
        {
          // salvia-775: ata de 06/08 — "agendado fica azul, confirmado fica
          // verde". Ate aqui as duas coisas eram a MESMA coluna: o backend
          // fundia 'scheduled' em 'confirmed' antes de chegar aqui, entao um
          // paciente confirmado aparecia numa coluna chamada "Agendados",
          // pintada de azul — o oposto do pedido. A fusao foi removida no
          // EventController; agora cada status tem a propria coluna.
          title: "Agendados",
          status: "scheduled",
          tasks: [],
          icon: "mdi-calendar-clock",
          iconColor: "blue",
          chipColor: "blue",
          colorClass: "blue lighten-5",
          emptyIcon: "mdi-calendar-blank",
          emptyText: "Nenhuma consulta agendada",
        },
        {
          title: "Confirmados",
          status: "confirmed",
          tasks: [],
          icon: "mdi-calendar-check",
          iconColor: "green",
          chipColor: "green",
          colorClass: "green lighten-5",
          emptyIcon: "mdi-calendar-check-outline",
          emptyText: "Nenhuma consulta confirmada",
        },
        {
          title: "Em Espera",
          status: "waiting",
          tasks: [],
          icon: "mdi-seat",
          iconColor: "amber darken-2",
          chipColor: "amber darken-2",
          colorClass: "amber lighten-5",
          emptyIcon: "mdi-seat-outline",
          emptyText: "Ninguém em espera",
        },
        {
          title: "Em Atendimento",
          status: "pending",
          tasks: [],
          icon: "mdi-account-clock",
          iconColor: "orange",
          chipColor: "orange",
          colorClass: "orange lighten-5",
          emptyIcon: "mdi-clock-alert",
          emptyText: "Nenhum atendimento em andamento",
        },
        {
          // salvia-775: "Confirmados" (acima) tambem e verde — sao dois
          // status diferentes e precisam ler como diferentes. Este fica num
          // tom mais escuro (darken-3) e com o fundo do cabecalho um degrau
          // mais forte (lighten-3 em vez de lighten-5), pra a diferenca
          // aparecer sem precisar olhar so pro icone.
          title: "Atendidos",
          status: "concluded",
          tasks: [],
          icon: "mdi-check-circle",
          iconColor: "green darken-3",
          chipColor: "green darken-3",
          colorClass: "green lighten-3",
          emptyIcon: "mdi-check-all",
          emptyText: "Nenhum atendimento concluído",
        },
        {
          title: "Falta",
          status: "no_show",
          tasks: [],
          icon: "mdi-account-off",
          iconColor: "blue-grey darken-1",
          chipColor: "blue-grey darken-1",
          colorClass: "blue-grey lighten-5",
          emptyIcon: "mdi-account-off-outline",
          emptyText: "Nenhuma falta registrada",
        },
        {
          title: "Cancelados",
          status: "canceled",
          tasks: [],
          icon: "mdi-close-circle",
          iconColor: "red",
          chipColor: "red",
          colorClass: "red lighten-5",
          emptyIcon: "mdi-cancel",
          emptyText: "Nenhuma consulta cancelada",
        },
      ],
    };
  },

  computed: {
    tourId() {
      return "kanban";
    },

    isHistory() {
      return this.selectedDate !== this.today;
    },
  },

  watch: {
    selectedDate() {
      this.loadBoard();
    },
  },

  mounted() {
    this.loadBoard();
    // Tela aberta na virada da meia-noite: sem isto o quadro de "hoje" seguiria
    // com o dia de ontem até alguém mexer. A checagem é local (sem rede); só
    // recarrega quando o dia de fato muda.
    this.dayWatcher = setInterval(() => {
      if (todayISO() !== this.today) this.loadBoard();
    }, 60000);
    this.ouvirMudancasDeStatus();
  },

  beforeDestroy() {
    clearTimeout(this.recargaPendente);
    clearInterval(this.dayWatcher);
    try {
      this.canalDoMedico?.stopListening(".EventStatusChanged", this.aoMudarStatus);
    } catch {
      // canal já fechado no logout
    }
  },

  methods: {
    calculateAge: (v) => calculateAge(v, { fallback: "N/A" }),
    // o evento carrega o próprio fuso; sem ele, usa o do navegador
    formatDate: (v, tz) => formatDateShort(v, { timeZone: tz || undefined }),
    formatTime: (v, tz) => formatTime(v, { timeZone: tz || undefined }),
    // Passos do tour (o auto-start e o "?" vêm do tourMixin).
    tourSteps() {
      return [
        {
          anchor: "kb-board",
          title: "Kanban do dia",
          description:
            "Esta é a visão do dia em quadro: os atendimentos de hoje organizados em colunas por status. Ótima para acompanhar o fluxo do consultório em tempo real.",
        },
        {
          anchor: "kb-dia",
          title: "Histórico do quadro",
          description:
            "O kanban sempre abre em hoje. Use as setas ou o calendário para ver como ficou o quadro de um dia que já passou — só os dias com consultas ficam disponíveis. Dias passados são só para consulta: os cartões não se movem.",
        },
        {
          anchor: "kb-colunas",
          title: "Colunas por status",
          description:
            "Cada coluna é um status do atendimento — Agendados, Confirmados, Em Espera, Em Atendimento, Atendidos, Falta e Cancelados. O número no topo de cada coluna mostra quantos atendimentos há nela.",
        },
        {
          anchor: "kb-card",
          title: "Arraste para mudar o status",
          description:
            "Cada cartão é um atendimento. Arraste-o de uma coluna para outra para atualizar o status — por exemplo, de “Em Espera” para “Em Atendimento” quando o paciente entrar. A mudança é salva automaticamente.",
        },
      ];
    },

    /**
     * salvia-779: o card muda de coluna sozinho quando o status muda em outro
     * lugar — o atendimento abrindo ou salvando, outra aba, outro aparelho.
     *
     * Canal privado do médico, o mesmo que as notificações já assinam no login
     * (store `notifications`); `private()` devolve o canal existente. Sem
     * Echo saudável o kanban só deixa de ser ao vivo, como era antes.
     */
    ouvirMudancasDeStatus() {
      const userId = this.$store.getters["auth/userId"];
      if (!userId) return;
      try {
        this.canalDoMedico = initEcho().private(`App.Models.User.${userId}`);
        this.canalDoMedico.listen(".EventStatusChanged", this.aoMudarStatus);
      } catch (e) {
        console.warn("[kanban] tempo real indisponível:", e);
      }
    },

    /**
     * Recarrega o dia em vez de mover o card pelo payload: o payload só traz
     * ids (sem dado cifrado no Soketi), e a API já resolve tudo. O atraso curto
     * junta rajadas — o próprio arrastar daqui também gera um aviso.
     */
    aoMudarStatus() {
      clearTimeout(this.recargaPendente);
      this.recargaPendente = setTimeout(() => this.loadBoard(), 400);
    },

    clearColumns() {
      this.columns.forEach((col) => (col.tasks = []));
    },

    /**
     * Monta o quadro do dia escolhido: hoje pela rota de sempre, um dia passado
     * pelo histórico (EventHistoryController), no MESMO formato de card.
     *
     * O tempo real continua ligado nos dois casos: num dia passado, só muda
     * algo se alguém corrigir um status daquele dia em outra tela — e aí o
     * histórico deve mostrar a correção.
     */
    async loadBoard() {
      // Virou a meia-noite com a tela aberta: "hoje" anda junto.
      const wasToday = this.selectedDate === this.today;
      this.today = todayISO();
      if (wasToday && this.selectedDate !== this.today) {
        this.selectedDate = this.today; // o watcher recarrega
        return;
      }

      const seq = ++this.loadSeq;
      if (this.isHistory) {
        try {
          const data = await fetchKanbanDay(this.selectedDate);
          if (seq !== this.loadSeq || !data?.success) return;
          await this.fillBoard(data.events || [], seq);
        } catch (e) {
          this.erroDeRequisicao(e, "Erro ao carregar o histórico do dia");
        }
        return;
      }

      await this.fetchTodayEvents(seq);
    },

    async fetchTodayEvents(seq) {
      try {
        // O "hoje" do kanban é o dia de quem está olhando, não o do
        // servidor (que roda em UTC). Sem enviar o fuso, das 21h em diante o
        // médico no Brasil veria as consultas de amanhã no lugar das de hoje.
        //
        // Data e hora nos cards seguem o MESMO fuso, o do navegador — que é o
        // que formatDate/formatTime usam por padrão e o que a grade do
        // calendário já fazia. Mostrar a hora no fuso do evento colocaria um
        // card "de hoje" exibindo a data de ontem.
        const { data } = await http.get("/api/events/today", {
          params: { time_zone: userTimeZone() },
        });

        if (seq !== this.loadSeq || !data?.success) return;

        await this.fillBoard(data.events || [], seq);
      } catch (e) {
        this.erroDeRequisicao(e, "Erro ao carregar consultas");
      }
    },

    async fillBoard(events, seq) {
      // buscar imagens em paralelo (mais rápido)
      const enrichedEvents = await Promise.all(
        events.map(async (event) => {
          if (event.patient?.photo) {
            try {
              event.patient.photo = await getProfileImage(event.patient.photo);
            } catch {
              // se falhar, mantém o que veio
            }
          }
          return event;
        }),
      );

      if (seq !== this.loadSeq) return;

      this.clearColumns();
      this.boardEvents = enrichedEvents;
      for (const event of enrichedEvents) {
        const column = this.columns.find(
          (col) => col.status === event.status,
        );
        if (column) column.tasks.push(event);
      }
    },

    /** Num dia passado, o card abre só para leitura (com o atalho da consulta). */
    openHistoryEvent(task) {
      if (!this.isHistory) return;
      this.historyEvent = task;
      this.historyDialog = true;
    },

    async onDragEnd(evt) {
      // evt: { from, to, item, oldIndex, newIndex, ... }
      const toStatus = evt.to?.dataset?.status;
      const fromStatus = evt.from?.dataset?.status;

      if (!toStatus || !fromStatus || toStatus === fromStatus) return;

      // item movido está agora dentro da lista do target
      const targetColumn = this.columns.find((c) => c.status === toStatus);
      const movedTask = targetColumn?.tasks?.[evt.newIndex];
      if (!movedTask) return;

      const oldStatus = movedTask.status;
      const newStatus = toStatus;

      if (newStatus === oldStatus) return;

      try {
        await http.put(`/api/events/${movedTask.id}/status`, {
          status: newStatus,
        });

        movedTask.status = newStatus;
        this.success(`Status alterado para "${targetColumn.title}"`);
      } catch (e) {
        this.erroDeRequisicao(e, "Erro ao atualizar status");
        await this.loadBoard(); // rollback visual
      }
    },

    extractConsultationType(description) {
      if (!description) return "Consulta";
      const match = description.match(/Tipo de consulta:\s*([^-]+)/);
      return match ? match[1].trim() : "Consulta";
    },

    openWhatsApp(task) {
      const phoneRaw = task?.patient?.phone;
      if (!phoneRaw) {
        this.warning("Número de telefone não disponível");
        return;
      }

      const phone = String(phoneRaw).replace(/\D/g, "");
      const message = `Olá ${task.patient.name}, esta é uma mensagem sobre sua consulta.`;
      const url = `https://wa.me/55${phone}?text=${encodeURIComponent(
        message,
      )}`;
      window.open(url, "_blank");
    },

    openPatientProfile(task) {
      const id = task?.patient?.id;
      if (!id) return;
      this.$router.push(`/patient-profile-dashboard/${id}`);
    },

    async markAsCompleted(task) {
      if (!task?.id) return;

      try {
        await http.put(`/api/events/${task.id}/status`, {
          status: "concluded",
        });
        await this.loadBoard();
        this.success("Consulta marcada como concluída");
      } catch (e) {
        this.erroDeRequisicao(e, "Erro ao marcar como concluída");
      }
    },
  },
};
</script>

<style scoped>
/* Barra do dia: mesmo cartão das colunas (borda, raio, sombra) e alinhada a
   elas. Em tela estreita o seletor desce para a linha de baixo, à direita. */
.kanban-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-bottom: 8px;
  padding: 10px 12px 10px 18px;
  background: var(--ds-surface-white);
  border: 1px solid var(--ds-blue-100);
  border-radius: 14px;
  box-shadow: 0 2px 10px rgba(31, 77, 63, 0.06);
}

.kanban-toolbar__title {
  display: flex;
  align-items: center;
  font-size: 1.05rem;
  font-weight: 700;
  white-space: nowrap;
}

.kanban-toolbar__badge {
  margin-left: 10px;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: var(--ds-primary);
  background: var(--ds-blue-50);
  border: 1px solid var(--ds-blue-100);
}

.kanban-toolbar__picker {
  margin-left: auto;
}

/* Sem espaço para tudo numa linha (a barra lateral da agenda ocupa 300px):
   título e seletor ficam juntos na primeira linha, e os totais descem para
   uma linha só deles — em vez de o seletor cair sozinho embaixo. */
@media (max-width: 1599px) {
  .kanban-toolbar__title {
    order: 1;
  }

  .kanban-toolbar__picker {
    order: 2;
  }

  .kanban-toolbar__summary {
    order: 3;
    flex-basis: 100%;
  }
}

/* "Em Atendimento" quebrava em "Atendiment / o" em telas médias: o título da
   coluna fica numa linha só e corta com reticências se faltar espaço. */
.column-header {
  font-size: 1.1rem !important;
}

.column-header .d-flex.align-center {
  min-width: 0;
}

.column-header .font-weight-bold {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Dia passado: o cartão não arrasta, abre os detalhes. */
.task-card.task-card--readonly,
.task-card.task-card--readonly:active {
  cursor: pointer;
}

.kanban-col {
  display: flex;
  flex-direction: column;
}

.kanban-column-card {
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 14px !important;
  border: 1px solid var(--ds-blue-100) !important;
  box-shadow: 0 2px 10px rgba(31, 77, 63, 0.06) !important;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.kanban-column-card:hover {
  border-color: var(--ds-blue-200) !important;
  box-shadow: 0 6px 20px rgba(31, 77, 63, 0.12) !important;
}

.column-header {
  padding: 14px 18px !important;
  border-radius: 14px 14px 0 0 !important;
  position: relative;
  overflow: hidden;
}

.column-header::before {
  content: "";
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px);
  background-size: 24px 24px;
  opacity: 0.5;
  pointer-events: none;
}

.w-100 {
  width: 100%;
}

/* Altura FIXA da área de cards: não cresce com os cards — quando eles passam
   do limite, aparece a rolagem dentro da coluna. A altura acompanha a tela:
   as duas fileiras de colunas cabem inteiras na janela (em 1080px de altura
   isso dá ~386px, perto do padrão de antes). Os 308px são o que fica fora das
   áreas de cards: cabeçalho do app, barra do dia, os títulos das duas
   fileiras e os espaçamentos (medidos no app). O piso de 340px mostra um card
   inteiro (278px com as margens) e o topo do segundo, com o nome do paciente
   — o suficiente para perceber que há mais cards embaixo. */
.kanban-column-content {
  flex: none;
  height: max(340px, calc((100vh - 308px) / 2));
  overflow-y: auto;
  overflow-x: hidden;
  padding: 8px;
  background: linear-gradient(180deg, #fafcff 0%, var(--ds-surface-white) 100%);
}

.draggable-list {
  min-height: 100px;
}

.task-card {
  /* salvia-777: 6px em cima e embaixo = 12px entre dois cards, metade dos
     24px de antes (eram 12px de margem de cada lado, com a classe ma-3).
     O recuo lateral e menor que o vertical de proposito: na coluna os
     cards se empilham, entao o que incomodava era o espaco de cima. */
  margin: 6px 8px;
  cursor: grab;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  border-radius: 10px !important;
  border: 1px solid var(--ds-blue-100) !important;
  border-left: 4px solid var(--ds-primary) !important;
  background: var(--ds-surface-white);
  box-shadow: 0 1px 4px rgba(31, 77, 63, 0.04) !important;
}

.task-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(31, 77, 63, 0.16) !important;
  border-color: var(--ds-blue-200) !important;
  border-left-color: var(--ds-primary) !important;
}

.task-card:active {
  cursor: grabbing;
}

.ghost-card {
  opacity: 0.5;
  background: var(--ds-blue-50);
  border: 2px dashed var(--ds-primary) !important;
}

.info-row {
  display: flex;
  align-items: center;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.empty-state :deep(.v-icon) {
  color: var(--ds-blue-200) !important;
}

.kanban-column-content::-webkit-scrollbar {
  width: 6px;
}

.kanban-column-content::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.kanban-column-content::-webkit-scrollbar-thumb {
  background: var(--ds-blue-200);
  border-radius: 10px;
}

.kanban-column-content::-webkit-scrollbar-thumb:hover {
  background: var(--ds-primary);
}

/* Tela estreita: as colunas se empilham (duas por linha ou uma só), então
   a conta das "duas fileiras" não vale — altura fixa pela tela, maior. */
@media (max-width: 959px) {
  .kanban-column-content {
    height: max(320px, calc(100vh - 360px));
  }
}
</style>
